import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import type { CreateSnippetApiArg } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_SAVE_SNIPPET_SERVER } from '@/app/module/user/definitions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

type SaveSnippetPayload = {
  item: CreateSnippetApiArg['body']
}

export const useSaveSnippet = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [createSnippet, mutationResult] = esApi.useCreateSnippetMutation()

  const saveSnippet = useCallback(
    async ({ item }: SaveSnippetPayload) => {
      if (!orgId) {
        return false
      }

      try {
        const response = await createSnippet({
          orgId: orgId.toString(),
          body: item,
        }).unwrap()
        dispatch(
          createNotification({
            'save-snippet-success': {
              type: 'success',
              message: 'Added to library.',
            },
          }),
        )
        return response
      } catch (error) {
        dispatch(
          createNotification({
            'save-snippet-error': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SAVE_SNIPPET_SERVER),
            },
          }),
        )
        return false
      }
    },
    [createSnippet, dispatch, orgId],
  )

  return [saveSnippet, mutationResult] as const
}
